<template>
  <v-row dense v-if="initialized && !productsLoading">
    <v-col cols="12" lg="7" xl="6">
      <DetailCard :title="$t('accountProducts')" :style="{ borderRadius: '0px 4px 4px 4px' }">
        <template #title.append v-if="crud">
          <Icon icon="mdi-pencil" :small="false" @icon-clicked="openDialog"/>
        </template>
        <v-row>
          <v-col cols="12" lg="6">
            <v-row>
              <v-col cols="12">
                <DataTable
                containerClass="pa-0"
                smallHeaders
                :loading="productsLoading"
                :showSearch="false"
                :headers="productTableHeaders"
                :customCells="[{ value: 'isScalable', slotName: 'is-scalable-cell' }]"
                :items="accountProducts"
                @product-chosen="onProductChosen">
                  <template #is-scalable-cell="{ item }">
                    <Icon
                    v-if="item.isScalable"
                    icon="mdi-ruler-square"
                    iconColor="secondary"
                    :tooltipText="$t('scalable')"/>
                  </template>
                </DataTable>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </DetailCard>
    </v-col>

    <v-col cols="12" lg="5" xl="6">
      <DetailCard>
        <v-col cols="12">
          <v-row no-gutters>
            <v-col cols="12">
              <ProductAutocomplete
              v-if="crud"
              :label="$t('fallbackProduct')"
              :productId="account.fallbackProductId"
              :excludedProducts="excludedFallbackProducts"
              limitToScalingSpecs
              hideRefresh
              :clearable="fallbackIsClearable"
              :propFetchProducts="false"
              @product-chosen="onProductChosen" />

              <v-text-field v-else outlined readonly :label="$t('fallbackProduct')" v-model="account.fallbackProduct.name"/>
            </v-col>
            <v-col cols="12">
              <v-checkbox :label="$t('excludeFromExpansion')" :readonly="!crud" v-model="excludeFallbackFromExpansion"
                @change="onExcludeFromExpansionChanged">
              </v-checkbox>
            </v-col>
          </v-row>
        </v-col>
      </DetailCard>
    </v-col>

    <Dialog :stateId="dialogId" @close="closeDialog" @dialog-closing="closeDialog" @click:outside="closeDialog">
      <AccountProductsForm v-if="accountProductsFormOpen" :account="account" @products-chosen="onAccountProductsChosen"/>
    </Dialog>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { uniqueDialogId } from '@/utils/componentHelpers.js'
import AccountProductHeaders from '@/headers/AccountProduct'

export default {
  name: 'AccountProducts',

  components: {
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    ProductAutocomplete: () => import('@/components/autocomplete/ProductAutocomplete.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    AccountProductsForm: () => import('@/components/account/account-scaling/products/AccountProductsForm.vue')
  },

  props: {
    crud: {
      type: Boolean,
      required: false,
      default: false
    },

    account: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    productsFetched: false,
    excludeFallbackFromExpansion: false,
    fallbackProduct: null,
    dialogId: uniqueDialogId('account-products'),
    accountProductsFormOpen: false,
    initialized: false
  }),

  computed: {
    ...mapGetters('product', ['allProducts', 'productsLoading']),

    accountProducts () {
      return (this.account.accountProducts ?? []).map(this.normalizeProductRow)
    },

    scaleAccountProducts () {
      return this.accountProducts.filter(p => p.isScalable)
    },

    productTableHeaders () {
      return AccountProductHeaders.accountProductHeaders()
    },

    excludedFallbackProducts () {
      return this.allProducts.filter(p => this.accountProducts.some(ap => ap.productId === p.productId)).map(p => p.name)
    },

    fallbackIsClearable () {
      return this.scaleAccountProducts.length === 0
    }
  },

  mounted () {
    this.getProducts()
    this.excludeFallbackFromExpansion = !this.account.fallbackExpands
    this.fallbackProduct = this.allProducts.find(p => p.name === this.account.fallbackProduct)
    this.initialized = true
  },

  methods: {
    ...mapActions('product', ['fetchProducts']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    getProducts () {
      if (!this.productsFetched) {
        this.productsFetched = true
        this.fetchProducts()
      }
    },

    normalizeProductRow (row) {
      return { ...row, name: row.name ?? row.product }
    },

    onProductChosen (product) {
      this.fallbackProduct = product
      this.$emit('fallback-product-chosen', product)
    },

    onExcludeFromExpansionChanged () {
      this.$emit('exclude-from-expansion-changed', this.excludeFallbackFromExpansion)
    },

    closeDialog () {
      this.accountProductsFormOpen = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    openDialog () {
      this.accountProductsFormOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    onAccountProductsChosen (products) {
      this.$emit('products-chosen', products)
      this.closeDialog()
    }
  }
}
</script>
